import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Apply() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">
        📋 JavaScript{" "}
        <strong style={{ color: "red" }}> Function.apply() </strong>Method
      </h1>

      <p className="tutorial-intro">
        The <code className="highlight_code">apply()</code> method invokes a
        function immediately and explicitly sets the{" "}
        <code className="highlight_code">this</code> value. Unlike{" "}
        <code className="highlight_code">call()</code>, it accepts arguments as
        an array.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code">apply()</code> invokes a function
            immediately with a specified{" "}
            <code className="highlight_code">this</code> value.
          </li>
          <li>Arguments must be passed as an array (or array-like object).</li>
          <li>Useful for method borrowing or dynamic argument passing.</li>
          <li>
            Difference from <code className="highlight_code">call()</code>:
            apply → array of arguments, call → argument list.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div className="tutorial-section">
        <h3>
          1️⃣ Basic <code className="highlight_code">apply()</code> Example
        </h3>
        <p>
          You can invoke a function and pass the{" "}
          <code className="highlight_code">this</code> value along with
          arguments as an array.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function greet(greeting) {
  console.log(greeting + ", I'm " + this.name);
}

const person = { name: "Alice" };

greet.apply(person, ["Hello"]);`}
          />
          <TryItButton
            code={`function greet(greeting){console.log(greeting+", I'm "+this.name);}const person={name:"Alice"};greet.apply(person,["Hello"]);`}
          />
        </div>
        <OutputBox>Output: Hello, I'm Alice</OutputBox>
      </div>

      {/* 2️⃣ Borrowing Methods */}
      <div className="tutorial-section">
        <h3>
          2️⃣ Borrowing Methods with{" "}
          <code className="highlight_code">apply()</code>
        </h3>
        <p>
          You can borrow a method from one object and apply it to another using
          an array of arguments.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person1 = {
  name: "Bob",
  greet: function(greeting) {
    console.log(greeting + ", " + this.name);
  }
};

const person2 = { name: "Charlie" };

person1.greet.apply(person2, ["Hi"]);`}
          />
          <TryItButton
            code={`const person1={name:"Bob",greet:function(greeting){console.log(greeting+", "+this.name);}};const person2={name:"Charlie"};person1.greet.apply(person2,["Hi"]);`}
          />
        </div>
        <OutputBox>Output: Hi, Charlie</OutputBox>
      </div>

      {/* 3️⃣ Using Built-in Functions */}
      <div className="tutorial-section">
        <h3>
          3️⃣ Using <code className="highlight_code">apply()</code> with Built-in
          Functions
        </h3>
        <p>
          <code className="highlight_code">apply()</code> is useful for passing
          arrays to functions that normally accept a list of arguments.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const numbers = [5, 10, 15];

console.log(Math.max.apply(null, numbers));`}
          />
          <TryItButton
            code={`const numbers=[5,10,15];console.log(Math.max.apply(null,numbers));`}
          />
        </div>
        <OutputBox>Output: 15</OutputBox>
      </div>

      {/* 4️⃣ call() vs apply() */}
      <div className="tutorial-section">
        <h3>4️⃣ call() vs apply()</h3>
        <p>
          <code className="highlight_code">call()</code> passes arguments
          individually, while <code className="highlight_code">apply()</code>{" "}
          expects an array.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function sum(a, b, c) {
  return a + b + c;
}

const nums = [1, 2, 3];

console.log(sum.call(null, 1, 2, 3)); // call: individual arguments
console.log(sum.apply(null, nums));   // apply: array of arguments`}
          />
          <TryItButton
            code={`function sum(a,b,c){return a+b+c;}const nums=[1,2,3];console.log(sum.call(null,1,2,3));console.log(sum.apply(null,nums));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            {`6
6`}
          </pre>
        </OutputBox>
      </div>

      {/* 🚀 Next Step */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Step</h2>
        <p>
          Now, you can explore <strong>Function.bind()</strong> which creates a
          new function with a permanently bound{" "}
          <code className="highlight_code">this</code> value.
        </p>
      </div>
    </div>
  );
}
